import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import moment from "moment";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";

import { useNavigate, useParams } from "react-router-dom"; // Import useNavigate
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { formatNumber } from "../../../shared/utils/formatNumber.js";
import PaintingReportModal from "./PaintingReportModal.js";

const Header = ({ CampaignDetails, productType }) => {
  const [finalBudgetValue, setFinalBudgetValue] = useState(0);
  const [finalCreativeSpread, setCreativeSpread] = useState(0);
  const [finalTotalStore, setFinalTotalStore] = useState(0);
  const [finalApprovedProducts, setFinalApprovedProducts] = useState(0);
  const [paintingModalOpen, setPaintingModalOpen] = useState(false);
  const userRole = localStorage.getItem("role");
  const getFinalBudget = () => {
    if (productType.length === 0 || productType.includes("All")) {
      return CampaignDetails?.data?.budget?.totalBudget || 0;
    } else {
      let sumBudget = 0;

      Object.entries(CampaignDetails?.data?.budget || {}).forEach(
        ([type, value]) => {
          if (type !== "totalBudget" && productType.includes(type)) {
            sumBudget += value;
          }
        },
      );

      return sumBudget;
    }
  };
  const getFinalCreativeSpread = () => {
    if (productType.length === 0 || productType.includes("All")) {
      return CampaignDetails?.data?.totalPaintedArea?.totalCreativeSpread || 0;
    } else {
      let sumCreativeSpread = 0;
      Object.entries(CampaignDetails?.data?.totalPaintedArea || {}).forEach(
        ([type, value]) => {
          if (productType.includes(type)) {
            sumCreativeSpread += value;
          }
        },
      );
      return sumCreativeSpread;
    }
  };

  const getFinalStores = () => {
    if (productType.length === 0 || productType.includes("All")) {
      return CampaignDetails?.totalStores?.totalApprovedProducts || 0;
    } else {
      let sumTotalStores = 0;
      Object.entries(CampaignDetails?.totalStores || {}).forEach(
        ([type, value]) => {
          if (
            ![
              "totalFinalStores",
              "totalApprovedProducts",
              "completedStores",
            ].includes(type) &&
            productType?.includes(type)
          ) {
            sumTotalStores += value;
          }
        },
      );
      return sumTotalStores;
    }
  };

  const getFinalApprovedProducts = () => {
    if (productType.length === 0 || productType.includes("All")) {
      return CampaignDetails?.totalStores?.totalApprovedProducts || 0;
    } else {
      const barGraphData = CampaignDetails?.barGraphData || [];
      const approvedProductIds = new Set(
        CampaignDetails?.approvedProductIds || [],
      );

      // Filter by selected product types
      const filteredData = barGraphData.filter((item) =>
        productType.includes(item.productType),
      );

      let approvedCount = 0;

      filteredData.forEach((product) => {
        // Skip if no services
        if (!product.servicesCount || !Array.isArray(product.servicesCount)) {
          return;
        }

        // Check if this product type has Recce service
        const hasRecce = product.servicesCount.some(
          (s) => s.category === "Recce" && s.totalJobs > 0,
        );

        if (hasRecce) {
          // For Recce: ONLY check if productId is in approvedProductIds (clientStatus === 1)
          if (approvedProductIds.has(product.productId)) {
            approvedCount++;
          }
        } else {
          // For non-Recce (Painting, Fabrication, Printing, Installation):
          // Need 100% completion
          const hasCompleteService = product.servicesCount.some(
            (service) =>
              service.totalJobs > 0 &&
              service.completedJobs === service.totalJobs,
          );

          if (hasCompleteService) {
            approvedCount++;
          }
        }
      });

      return approvedCount;
    }
  };
  useEffect(() => {
    const totalBudget = getFinalBudget();
    setFinalBudgetValue(totalBudget);

    const totalCreativeSpread = getFinalCreativeSpread();
    setCreativeSpread(totalCreativeSpread);

    const totalStores = getFinalStores();
    setFinalTotalStore(totalStores);

    const totalApprovedProducts = getFinalApprovedProducts();
    setFinalApprovedProducts(totalApprovedProducts);
  }, [productType, CampaignDetails]);
  const navigate = useNavigate(); // Initialize navigation
  const { id: baseId } = useParams();
  const [recceLoading, setRecceLoading] = useState(false);
  const handleRecceDownload = async () => {
    if (CampaignDetails?.data?.servicesNeeded?.includes("Recce")) {
      try {
        setRecceLoading(true);

        const token = localStorage.getItem("token");

        const response = await axios.get(
          `${process.env.REACT_APP_URL}/excelData/recceJobs/${CampaignDetails?.data?._id}`,
          {
            params: {
              requestedBy: "admin",
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: "blob",
          },
        );

        // 🔥 DEBUG START

        const contentDisposition = response.headers["content-disposition"];

        let fileName = "RecceJobs.xlsx";

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);

          if (match && match[1]) {
            fileName = match[1];
          }
        }

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);

        setRecceLoading(false);
      } catch (error) {
        console.error("❌ Error downloading Excel:", error);
        setRecceLoading(false);
      }
    } else {
      try {
        setRecceLoading(true);

        const token = localStorage.getItem("token");

        const response = await axios.get(
          `${process.env.REACT_APP_URL}/excelData/paintingJobs/${CampaignDetails?.data?._id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: "blob",
          },
        );

        // 🔥 DEBUG START

        const contentDisposition = response.headers["content-disposition"];

        let fileName = "RecceJobs.xlsx";

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);

          if (match && match[1]) {
            fileName = match[1];
          }
        }

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);

        setRecceLoading(false);
      } catch (error) {
        console.error("❌ Error downloading Excel:", error);
        setRecceLoading(false);
      }
    }
  };

  const stats = [
    ...(userRole === "-1" || userRole === "1"
      ? [
          {
            id: 0,
            title: "Approved Spend",
            value: finalBudgetValue,
            icon: "/assests/images/Campaigndash/Icon.png",
            description:
              "The total allocated budget for the campaign, including all expenditures.",
          },
        ]
      : []),
    {
      id: 1,
      title: "Creative Spread",
      value: finalCreativeSpread,
      icon: "/assests/images/Campaigndash/Icon (1).png",
      description:
        "The total area covered by creative assets like banners, posters, and signage.",
    },
    {
      id: 2,
      title: "Approved Products",
      value: finalApprovedProducts,
      icon: "/assests/images/Campaigndash/Icon (2).png",
      description:
        "The total count of all products approved for execution in this campaign.",
    },
    {
      id: 3,
      title: "IP's Engaged",
      value: CampaignDetails?.totalIpsEngaged,
      icon: "/assests/images/Campaigndash/Icon (3).png",
      description:
        "The number of implementation partners involved in executing the campaign.",
      servicesNeeded: ["Recce", "Installation"],
    },
    {
      id: 4,
      title: "VP's Engaged",
      value: CampaignDetails?.totalVendorEngaged,
      icon: "/assests/images/Campaigndash/Group 41.png",
      description:
        "The number of vendors and suppliers contributing to the campaign's execution.",
      servicesNeeded: ["Fabrication", "Printing", "Painting"],
    },
  ];

  const handleNavigation = (path) => {
    navigate(path); // Redirect to the specified path
  };

  const formatName = (name) => {
    if (!name) return "";

    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };
  const ownerName = formatName(CampaignDetails?.data?.campaignOwnerId?.name);
  const isLong = ownerName.length > 20;
  const displayName = isLong ? ownerName.slice(0, 20) + "..." : ownerName;

  const chipLabel = `Campaign owner: ${displayName}`;
  const tooltipTitle = `Campaign owner: ${ownerName}`;
  return (
    <div>
      <div className="bg-white shadow-md rounded-xl p-4 flex items-center space-x-4 justify-between">
        <h1 className="text-sm lg:text-2xl font-bold text-gray-800">
          {CampaignDetails?.data?.campaignTitle?.charAt(0).toUpperCase() +
            CampaignDetails?.data?.campaignTitle.slice(1)}
        </h1>
        <div className="flex items-center">
          <Stack direction="row" spacing={1}>
            {CampaignDetails?.data?.servicesNeeded?.includes("Painting") && (
              <div
                className="bg-gray-100 rounded-full  px-3 py-1 cursor-pointer flex justify-between items-center hover:shadow-md hover:scale-105 transform translate-x-1 duration-150 "
                onClick={() => setPaintingModalOpen(true)}
              >
                <button className="text-sm">Painting Report</button>
              </div>
            )}
            <PaintingReportModal
              open={paintingModalOpen}
              onClose={() => setPaintingModalOpen(false)}
              CampaignDetails={CampaignDetails}
              jobs={[]}
            />
            {
              <div
                className="bg-gray-100 rounded-full w-[155px] px-2 py-1 cursor-pointer flex justify-between items-center hover:shadow-md hover:scale-105 transform translate-x-1 duration-150 "
                onClick={handleRecceDownload}
              >
                <DownloadIcon sx={{ fontSize: "20px" }} />{" "}
                {recceLoading ? (
                  <CircularProgress size={20}></CircularProgress>
                ) : (
                  <button className="text-sm "> Campaign Report</button>
                )}
              </div>
            }
            {isLong ? (
              <Tooltip title={tooltipTitle}>
                <Chip label={chipLabel} />
              </Tooltip>
            ) : (
              <Chip label={chipLabel} />
            )}
            <Chip
              label={`${moment(CampaignDetails?.data?.startDate).format(
                "DD MMM YY",
              )} - ${moment(CampaignDetails?.data?.endDate).format(
                "DD MMM YY",
              )}`}
            />
          </Stack>
        </div>
      </div>
      <div className="grid sm:grid-cols-2  md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 mt-4">
        {stats.map((stat, index) => {
          return (
            <>
              {(!stat.servicesNeeded ||
                CampaignDetails?.data?.servicesNeeded?.some((ele) =>
                  stat.servicesNeeded.includes(ele),
                )) && (
                <div
                  key={index}
                  className="bg-white shadow-md rounded-xl p-4 flex items-center space-x-4 w-auto h-auto relative"
                >
                  <img
                    src={stat.icon}
                    alt={stat.title}
                    className="w-12 h-12 object-contain"
                  />

                  <div>
                    <p className="text-gray-400 text-xs">{stat.title}</p>
                    <p className="text-gray-800 font-semibold w-32 text-base flex items-center justify-between gap-2">
                      {stat.title === "Creative Spread"
                        ? `${stat.value?.toFixed(2)} Sq.Ft.`
                        : stat.title === "Approved Spend"
                          ? Number(stat.value?.toFixed(2)).toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )
                          : formatNumber(stat?.value)}
                    </p>
                  </div>

                  <Tooltip
                    title={
                      <div className="text-sm">
                        <p className="text-gray-300 text-center">
                          {stat?.description}
                        </p>
                      </div>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      className="absolute right-1 top-1 text-gray-400 text-sm cursor-pointer"
                    />
                  </Tooltip>
                </div>
              )}
            </>
          );
        })}

        <div
          className=" shadow-md rounded-xl flex flex-col relative"
          style={{
            background:
              "linear-gradient(90deg, rgba(148,169,255,1) 0%, rgba(65,140,255,1) 35%)",
          }}
        >
          <p className="text-white text-sm pt-4 pl-4">
            Admin Pending Approvals
          </p>
          <div className="flex justify-between items-center">
            <p className="text-white font-semibold text-lg pl-4 pb-4">
              {CampaignDetails?.pendingApprovalsAdmin || 0}
            </p>
            <img
              onClick={() =>
                handleNavigation(
                  `/adminportal/campaign/dashboard/${baseId}/reccejobApproveAdmin`,
                )
              }
              src="/assests/images/Campaigndash/arrow-up-right.png"
              alt="pending"
              className="w-10 h-10 object-contain pb-4 cursor-pointer"
            />
          </div>
          <Tooltip
            title={
              <div className="text-sm">
                {/* <p className="font-semibold text-center text-gray-300">{stat.value}</p> */}
                <p className="text-gray-300 text-center">Recce Approvals</p>
              </div>
            }
            arrow
            placement="top"
          >
            <InfoOutlinedIcon
              fontSize="small"
              className="absolute right-1 top-1 text-white text-sm cursor-pointer"
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default Header;
